/**
 * DeleteAgentModal Component
 * 
 * A modal dialog for deleting an agent with:
 * - Warning icon centered at top
 * - Title and description centered
 * - Input to confirm by typing agent name
 * - Cancel and Delete buttons
 * - Glass morphism styling
 */

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ReactComponent as CloseIcon } from '../../assets/images/close-icon.svg';
import LoadingSpinner from '../LoadingSpinner';

interface DeleteAgentModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Callback when modal is closed */
  onClose: () => void;
  /** Agent name to confirm deletion */
  agentName: string;
  /** Callback when delete is confirmed */
  onDelete: () => Promise<void>;
}

const DeleteAgentModal = ({ open, onClose, agentName, onDelete }: DeleteAgentModalProps) => {
  const [confirmName, setConfirmName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Check if name matches (case-sensitive)
  const isNameMatch = confirmName === agentName;

  // Reset state when modal opens
  useEffect(() => {
    if (open) {
      setConfirmName('');
      setError(null);
      setIsLoading(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Handle backdrop click
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget && !isLoading) {
      onClose();
    }
  };

  // Handle delete
  const handleDelete = async () => {
    if (!isNameMatch) return;

    setIsLoading(true);
    setError(null);

    try {
      await onDelete();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete agent');
      setIsLoading(false);
    }
  };

  // Handle key down
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isNameMatch && !isLoading) {
      e.preventDefault();
      handleDelete();
    } else if (e.key === 'Escape' && !isLoading) {
      e.preventDefault();
      onClose();
    }
  };

  if (!open) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999]"
        onClick={handleBackdropClick}
      />

      {/* Modal */}
      <div
        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[9999] animate-slide-in-center w-[calc(100vw-32px)] lg:w-auto"
      >
        <div className="w-full lg:w-[420px] bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-[0_8px_32px_rgba(0,0,0,0.5)] overflow-hidden">
          {/* Close button */}
          {!isLoading && (
            <button
              onClick={onClose}
              className="absolute right-4 top-4 text-white/40 hover:text-white/80 transition-colors z-10"
              aria-label="Close"
            >
              <CloseIcon width="14" height="14" />
            </button>
          )}

          {/* Content */}
          <div className="px-8 py-8">
            {/* Warning Icon */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" className="text-red-400">
                  <path d="M12 9v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
            </div>

            {/* Title */}
            <h2 className="text-xl font-semibold text-white text-center mb-2">
              Delete Agent?
            </h2>

            {/* Description */}
            <p className="text-sm text-white/60 text-center mb-6">
              This action cannot be undone. All data associated with this agent will be permanently deleted.
            </p>

            {/* Input */}
            <div className="mb-4">
              <label className="block text-sm text-white/60 text-center mb-3">
                <span>Enter "</span><span className="text-white font-medium">{agentName}</span><span>" to confirm.</span>
              </label>
              <input
                ref={inputRef}
                type="text"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Agent name"
                disabled={isLoading}
                className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white text-center placeholder-white/30 focus:outline-none focus:border-red-400/50 transition-colors disabled:opacity-50"
              />
            </div>

            {/* Error message */}
            {error && (
              <p className="text-sm text-red-400 text-center mb-4">{error}</p>
            )}

            {/* Buttons */}
            <div className="flex justify-center gap-3 mt-6">
              <button
                onClick={onClose}
                disabled={isLoading}
                className="px-6 py-2.5 text-sm font-medium text-white/70 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-colors disabled:opacity-50"
              >
                <span>Cancel</span>
              </button>
              <button
                onClick={handleDelete}
                disabled={isLoading || !isNameMatch}
                className="px-6 py-2.5 text-sm font-medium text-white bg-red-500 hover:bg-red-600 rounded-xl transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isLoading && <LoadingSpinner width="14px" height="14px" />}
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default DeleteAgentModal;
